// src/pages/ReceiptHistoryPage.js
import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

function ReceiptHistoryPage() {
  const [receipts, setReceipts] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendPhone, setResendPhone] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [resendError, setResendError] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchId, setSearchId] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const currencyCode = useCurrencyCode();

  const fetchReceipts = useCallback(async () => {
    try {
      const params = { page, limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (searchId) params.receiptId = searchId;

      const res = await api.get('/receipts', { params });
      const rows = Array.isArray(res.data)
        ? res.data
        : res.data && res.data.rows
        ? res.data.rows
        : [];
      const count =
        !Array.isArray(res.data) && res.data && typeof res.data.count === 'number'
          ? res.data.count
          : rows.length;

      setReceipts(rows);
      setTotalCount(count);
    } catch (err) {
      console.error('Error loading receipts:', err);
      toast.error('Failed to load receipts');
    }
  }, [page, startDate, endDate, searchId]);

  useEffect(() => {
    fetchReceipts();
    setSelectedReceipt(null);
    setResendEmail('');
    setResendPhone('');
    setResendError('');
  }, [fetchReceipts]);

  const viewReceipt = async (id) => {
    setLoadingReceipt(true);
    try {
      const res = await api.get(`/receipts/${id}`);
      setSelectedReceipt(res.data);
      setResendEmail(res.data.customerEmail || '');
      setResendPhone(res.data.customerPhone || '');
      setResendError('');
    } catch (err) {
      console.error('Error loading receipt details:', err);
      toast.error('Failed to load receipt details');
    } finally {
      setLoadingReceipt(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchId('');
    setPage(1);
  };

  const handleEmailReceipt = async () => {
    if (!selectedReceipt) return;
    const trimmed = resendEmail.trim();
    if (!/\S+@\S+\.\S+/.test(trimmed)) {
      setResendError('Enter a valid email address.');
      return;
    }
    setSendingEmail(true);
    setResendError('');
    try {
      await api.post(`/receipts/${selectedReceipt.id}/email`, {
        email: trimmed,
        customerName: selectedReceipt.customerName || undefined,
        customerPhone: selectedReceipt.customerPhone || undefined,
      });
      toast.success('Receipt emailed to customer.');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to send email.';
      setResendError(message);
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  const validatePhone = () => {
    const trimmed = resendPhone.trim();
    if (!/^\+?[0-9]{7,15}$/.test(trimmed)) {
      setResendError('Enter a valid phone number with country code.');
      return null;
    }
    setResendError('');
    return trimmed;
  };

  const handleWhatsAppReceipt = async () => {
    if (!selectedReceipt) return;
    const phone = validatePhone();
    if (!phone) return;
    setSendingWhatsApp(true);
    try {
      await api.post(`/receipts/${selectedReceipt.id}/whatsapp`, {
        phone,
        customerName: selectedReceipt.customerName || undefined,
        customerEmail: resendEmail.trim() || selectedReceipt.customerEmail || undefined,
      });
      toast.success('WhatsApp receipt sent.');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to send WhatsApp receipt.';
      setResendError(message);
      toast.error(message);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSmsReceipt = async () => {
    if (!selectedReceipt) return;
    const phone = validatePhone();
    if (!phone) return;
    setSendingSms(true);
    try {
      await api.post(`/receipts/${selectedReceipt.id}/sms`, {
        phone,
        customerName: selectedReceipt.customerName || undefined,
        customerEmail: resendEmail.trim() || selectedReceipt.customerEmail || undefined,
      });
      toast.success('SMS receipt sent.');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to send SMS receipt.';
      setResendError(message);
      toast.error(message);
    } finally {
      setSendingSms(false);
    }
  };

  const handlePrintReceipt = () => {
    if (!selectedReceipt) return;
    const printWindow = window.open('', '_blank', 'width=480,height=640');
    if (!printWindow) {
      toast.error('Pop-up blocked. Allow pop-ups to print receipts.');
      return;
    }
    const itemsHtml =
      selectedReceipt.items?.map(
        (item) => `
          <tr>
            <td style="padding:4px 0;">${item.productName}</td>
            <td style="padding:4px 0;text-align:center;">${item.quantity}</td>
            <td style="padding:4px 0;text-align:right;">${formatCurrency(item.unitPrice, currencyCode)}</td>
            <td style="padding:4px 0;text-align:right;">${formatCurrency(item.subtotal, currencyCode)}</td>
          </tr>`
      ).join('') || '';
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt #${selectedReceipt.id}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; color:#0f172a; padding:24px; }
            h1 { font-size:20px; margin-bottom:4px; }
            table { width:100%; border-collapse:collapse; font-size:13px; margin-top:12px; }
            th { text-align:left; text-transform:uppercase; letter-spacing:0.08em; font-size:11px; border-bottom:1px solid #cbd5f5; padding-bottom:4px; }
            td { font-size:13px; }
          </style>
        </head>
        <body>
          <h1>Ofoodi POS</h1>
          <p>Receipt #${selectedReceipt.id} • ${new Date(selectedReceipt.createdAt).toLocaleString()}</p>
          <p>Payment Method: <strong>${selectedReceipt.paymentMethod}</strong></p>
          <p>Total: <strong>${formatCurrency(selectedReceipt.total, currencyCode)}</strong></p>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th style="text-align:center;">Qty</th>
                <th style="text-align:right;">Each</th>
                <th style="text-align:right;">Subtotal</th>
              </tr>
            </thead>
            <tbody>${itemsHtml}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportCSV = async () => {
    try {
      const params = { limit: totalCount || 1000, page: 1 };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (searchId) params.receiptId = searchId;

      const res = await api.get('/receipts', { params });
      const allRows = Array.isArray(res.data)
        ? res.data
        : res.data.rows || [];

      let filtered = allRows;
      if (searchId) {
        filtered = filtered.filter((r) => String(r.id) === String(searchId));
      }
      if (startDate) {
        const sd = new Date(startDate);
        filtered = filtered.filter((r) => new Date(r.createdAt) >= sd);
      }
      if (endDate) {
        const ed = new Date(endDate);
        ed.setHours(23, 59, 59, 999);
        filtered = filtered.filter((r) => new Date(r.createdAt) <= ed);
      }

      const lines = [
        ['Receipt ID', 'Date', 'Payment Method', `Total (${currencyCode})`, `Discount (${currencyCode})`],
        ...filtered.map((r) => [
          r.id,
          new Date(r.createdAt).toLocaleString(),
          r.paymentMethod,
          formatCurrency(r.total, currencyCode),
          formatCurrency(r.totalDiscount, currencyCode),
        ]),
      ];

      const csvContent = lines.map((line) => line.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'receipts.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV');
    }
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-auto bg-slate-950 px-6 py-6 text-slate-100">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-slate-100">Receipt history</h1>
        <p className="text-sm text-slate-400">
          Review past receipts, drill into line items, and export summaries.
        </p>
      </header>

      <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Filters
        </h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-4">
          <label className="space-y-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Start date
            </span>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
            />
          </label>
          <label className="space-y-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              End date
            </span>
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
            />
          </label>
          <label className="space-y-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Receipt ID
            </span>
            <input
              type="number"
              value={searchId}
              onChange={(event) => setSearchId(event.target.value)}
              placeholder="Search by ID"
              className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
            />
          </label>
          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={applyFilters}
              className="inline-flex items-center justify-center rounded-full bg-brandGreen px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={exportCSV}
              className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
            >
              Export CSV
            </button>
          </div>
        </div>
      </section>

      <div className="grid flex-1 gap-6 lg:grid-cols-[1fr,1.2fr]">
        <section className="flex flex-col rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              Receipts
            </h2>
            <span className="text-xs text-slate-500">
              {totalCount} result{totalCount === 1 ? '' : 's'}
            </span>
          </header>
          <ul className="mt-4 flex-1 divide-y divide-slate-800 overflow-y-auto rounded-2xl border border-slate-800 bg-slate-950/40">
            {receipts.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-slate-500">
                No receipts found for the selected filters.
              </li>
            )}
            {receipts.map((receipt) => {
              const isActive = selectedReceipt?.id === receipt.id;
              return (
                <li key={receipt.id}>
                  <button
                    type="button"
                    onClick={() => viewReceipt(receipt.id)}
                    className={`flex w-full flex-col gap-1 px-4 py-3 text-left text-sm transition hover:bg-slate-900 ${
                      isActive ? 'bg-brandGreen/20 text-brandGreen-100' : 'text-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Receipt #{receipt.id}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(receipt.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{receipt.paymentMethod}</span>
                      <span>{formatCurrency(receipt.total, currencyCode)}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-950/50 px-3 py-1 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-slate-300">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page >= totalPages}
              className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-950/50 px-3 py-1 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          {loadingReceipt ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Loading receipt...
            </div>
          ) : selectedReceipt ? (
            <div className="flex h-full flex-col gap-4 overflow-auto">
              <header>
                <h2 className="text-lg font-semibold text-slate-100">
                  Receipt #{selectedReceipt.id}
                </h2>
                <p className="text-xs text-slate-500">
                  {new Date(selectedReceipt.createdAt).toLocaleString()}
                </p>
              </header>

              <div className="grid gap-2 rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-slate-300">
                <div className="flex justify-between">
                  <span className="text-slate-400">Payment method</span>
                  <span>{selectedReceipt.paymentMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Discount</span>
                  <span>{formatCurrency(selectedReceipt.totalDiscount, currencyCode)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-100">
                  <span>Total</span>
                  <span>{formatCurrency(selectedReceipt.total, currencyCode)}</span>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-200">
                <div className="flex flex-col gap-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</h3>
                  <p className="text-slate-100 text-base font-semibold">
                    {selectedReceipt.customerName || 'Not provided'}
                  </p>
                  <p className="text-xs text-slate-400">
                    Email: {selectedReceipt.customerEmail || '—'} • Phone: {selectedReceipt.customerPhone || '—'}
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handlePrintReceipt}
                    className="rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500"
                  >
                    Print receipt
                  </button>
                </div>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Customer phone
                    </label>
                    <input
                      type="tel"
                      value={resendPhone}
                      onChange={(e) => {
                        setResendPhone(e.target.value);
                        setResendError('');
                      }}
                      placeholder="+44 7123 456789"
                      className="w-full rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-2 text-sm text-slate-100 focus:border-brandGreen/50 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleWhatsAppReceipt}
                        disabled={sendingWhatsApp}
                        className="rounded-2xl border border-emerald-500/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-100 transition hover:bg-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {sendingWhatsApp ? 'Sending...' : 'Send WhatsApp'}
                      </button>
                      <button
                        type="button"
                        onClick={handleSmsReceipt}
                        disabled={sendingSms}
                        className="rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {sendingSms ? 'Sending...' : 'Send SMS'}
                      </button>
                    </div>
                  </div>

                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Email this receipt
                  </label>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => {
                        setResendEmail(e.target.value);
                        setResendError('');
                      }}
                      placeholder="customer@example.com"
                      className="w-full rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-2 text-sm text-slate-100 focus:border-brandGreen/50 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
                    />
                    <button
                      type="button"
                      onClick={handleEmailReceipt}
                      disabled={sendingEmail}
                      className="rounded-2xl bg-brandGreen px-5 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sendingEmail ? 'Sending...' : 'Send email'}
                    </button>
                  </div>
                  {resendError && <p className="text-xs text-rose-300">{resendError}</p>}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Items
                </h3>
                {selectedReceipt.items && selectedReceipt.items.length > 0 ? (
                  <ul className="mt-2 space-y-2 rounded-2xl border border-slate-800 bg-slate-950/40 p-3 text-sm text-slate-200">
                    {selectedReceipt.items.map((item, idx) => (
                      <li key={idx} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-100">{item.productName}</span>
                          <span className="text-xs text-slate-500">
                            {item.quantity} × {formatCurrency(item.unitPrice, currencyCode)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Barcode: {item.barcode || 'N/A'}</span>
                          <span>{formatCurrency(item.subtotal, currencyCode)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-500">
                    No items recorded for this receipt.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 text-sm text-slate-500">
              Select a receipt to view its details.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default ReceiptHistoryPage;
